// StepResults.jsx
// Final results screen — combines triage level, screening scores, body systems,
// and per-assessment recommendations into one comprehensive report
import { useRef } from "react";
import styles from "./StepResults.module.css";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────
const pct = (score, max) => (max > 0 ? Math.round((score / max) * 100) : 0);

const TRIAGE_META = {
  emergency: { color: "#DC2626", bg: "#FEF2F2", icon: "🚨", label: "Emergency"   },
  urgent:    { color: "#EA580C", bg: "#FFF7ED", icon: "⚠️", label: "Urgent Care" },
  semi:      { color: "#0284C7", bg: "#EFF6FF", icon: "🔵", label: "Semi-Urgent" },
  routine:   { color: "#16A34A", bg: "#F0FDF4", icon: "✅", label: "Non-Urgent"  },
};

const SEVERITY_COLOR = {
  Minimal:           "#16A34A",
  Mild:              "#65A30D",
  Moderate:          "#D97706",
  "Moderately Severe": "#EA580C",
  Severe:            "#DC2626",
};

// ─────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────
const Pill = ({ label, color }) => (
  <span style={{
    display: "inline-block", fontSize: 11, fontWeight: 700,
    padding: "2px 10px", borderRadius: 99,
    background: color + "20", color, border: `1px solid ${color}40`,
  }}>
    {label}
  </span>
);

const BarTrack = ({ value, max, color }) => (
  <div style={{ height: 7, background: "#E5E7EB", borderRadius: 99, overflow: "hidden", marginTop: 4 }}>
    <div style={{
      height: "100%", width: `${pct(value, max)}%`,
      background: color, borderRadius: 99,
      transition: "width 0.6s cubic-bezier(.4,0,.2,1)",
    }} />
  </div>
);

const SectionHeading = ({ icon, title }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
    <span style={{ fontSize: 18 }}>{icon}</span>
    <h3 style={{ fontSize: 15, fontWeight: 800, color: "#111827", margin: 0 }}>{title}</h3>
  </div>
);

// ─────────────────────────────────────────────────────────
// Triage Banner
// ─────────────────────────────────────────────────────────
function TriageBanner({ triage }) {
  const code  = triage?.triageCode || triage?.code || "routine";
  const meta  = TRIAGE_META[code] || TRIAGE_META.routine;
  const level = triage?.triageLevel || triage?.level || meta.label;

  return (
    <div className={styles.triageBanner} style={{ background: meta.bg, border: `1.5px solid ${meta.color}30` }}>
      <div className={styles.triageBannerIcon}>{meta.icon}</div>
      <div className={styles.triageBannerBody}>
        <div className={styles.triageBannerBadge} style={{ background: meta.color + "20", color: meta.color }}>
          {level}
        </div>
        <p className={styles.triageBannerHeadline}>
          {triage?.headline || "Your triage assessment is complete."}
        </p>
        <p className={styles.triageBannerTimeframe}>
          ⏱ Recommended timeframe: <strong>{triage?.timeframe || "As advised by your doctor"}</strong>
        </p>
        {triage?.clinicalNote && (
          <p className={styles.triageBannerNote}>📋 {triage.clinicalNote}</p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Screening Score Card
// ─────────────────────────────────────────────────────────
function ScreeningScoreCard({ result }) {
  if (!result) return null;
  const { title, shortTitle, totalScore, maxScore, severity, severityMeta, criticalFlags, needsClinicalAttention, completed, answeredCount } = result;
  const color = severityMeta?.color || SEVERITY_COLOR[severity] || "#6B7280";
  const bar   = pct(totalScore, maxScore);

  return (
    <div className={styles.scoreCard}>
      {/* Header */}
      <div className={styles.scoreCardHead}>
        <div>
          <div className={styles.scoreCardTitle}>{title}</div>
          <div className={styles.scoreCardSub}>{shortTitle} · {answeredCount} questions answered</div>
        </div>
        <Pill label={severity || "—"} color={color} />
      </div>

      {/* Score */}
      <div className={styles.scoreRow}>
        <span className={styles.scoreRaw} style={{ color }}>{totalScore}</span>
        <span className={styles.scoreMax}>/ {maxScore}</span>
        <span className={styles.scorePct} style={{ color }}>({bar}%)</span>
      </div>
      <BarTrack value={totalScore} max={maxScore} color={color} />

      {/* Recommendation */}
      {severityMeta?.recommendation && (
        <p className={styles.scoreRec}>{severityMeta.recommendation}</p>
      )}

      {/* Critical flag */}
      {criticalFlags?.length > 0 && (
        <div className={styles.criticalAlert}>
          🚨 <strong>Critical response detected.</strong> Immediate clinical attention is required.
        </div>
      )}

      {needsClinicalAttention && !criticalFlags?.length && (
        <div className={styles.attentionAlert}>
          ⚠️ Clinical evaluation is recommended based on this score.
        </div>
      )}

      {!completed && (
        <div className={styles.incompleteNote}>
          ⚪ This screening was not fully completed.
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Symptom Tag List
// ─────────────────────────────────────────────────────────
function SymptomList({ symptoms }) {
  return (
    <div className={styles.symptomList}>
      {symptoms.map((s) => (
        <span key={s.id || s.label} className={styles.symptomTag}>{s.label}</span>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Body System Row
// ─────────────────────────────────────────────────────────
function BodySystemRow({ sys }) {
  return (
    <div className={styles.bodyRow}>
      <div className={styles.bodyRowLeft}>
        <span className={styles.bodySystem}>{sys.system}</span>
        <span className={styles.bodyCount}>{sys.count} symptom{sys.count !== 1 ? "s" : ""}</span>
      </div>
      <div className={styles.bodyTags}>
        {sys.symptoms.map((s) => (
          <span key={s} className={styles.bodyTag}>{s}</span>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// computeBodySystems (same as StepTriage — no dep import)
// ─────────────────────────────────────────────────────────
function computeBodySystems(symptoms) {
  const map = {
    Cardiovascular:       ["Chest Pain", "Palpitations", "Shortness of Breath"],
    Neurological:         ["Headache", "Migraine", "Dizziness", "Numbness"],
    Digestive:            ["Nausea", "Stomach Pain", "Bloating", "Vomiting", "Constipation"],
    Respiratory:          ["Cough", "Wheezing", "Shortness of Breath"],
    Reproductive:         ["Pelvic Pain", "Irregular Periods", "Heavy Bleeding"],
    Musculoskeletal:      ["Back Pain", "Joint Pain", "Muscle Ache", "Stiffness"],
    "Mental & Emotional": ["Anxiety", "Depression", "Stress", "Insomnia", "Mood Swings"],
  };
  return Object.entries(map)
    .map(([system, keys]) => {
      const matched = symptoms.filter((s) =>
        keys.some((k) => s.label?.toLowerCase().includes(k.toLowerCase()))
      );
      return matched.length > 0
        ? { system, count: matched.length, symptoms: matched.map((m) => m.label) }
        : null;
    })
    .filter(Boolean);
}

// ─────────────────────────────────────────────────────────
// Recommendations from screening results
// ─────────────────────────────────────────────────────────
function buildRecommendations(triageCode, screening) {
  const base = {
    emergency: [
      "Go to the nearest emergency department immediately",
      "Call emergency services (108 / 112) if you cannot travel safely",
      "Do not drive yourself — ask someone to take you or call an ambulance",
    ],
    urgent: [
      "Contact your doctor or GP today",
      "Visit an urgent care clinic if your doctor is unavailable",
      "Do not delay — symptoms should be evaluated within 24 hours",
    ],
    semi: [
      "Book an appointment with your primary care physician within 2–3 days",
      "Monitor symptoms and note any changes",
      "Avoid self-medication without professional guidance",
    ],
    routine: [
      "Schedule a routine check-up with your doctor within 1–2 weeks",
      "Maintain a healthy lifestyle — adequate sleep, nutrition, and exercise",
      "Keep a symptom diary to share at your appointment",
    ],
  };

  const recs = [...(base[triageCode] || base.routine)];

  // Add assessment-specific recommendations
  const results = screening?.scoreData?.results || [];
  results.forEach((r) => {
    if (r?.severityMeta?.recommendation) {
      recs.push(`${r.shortTitle}: ${r.severityMeta.recommendation}`);
    }
  });

  return recs;
}

// ─────────────────────────────────────────────────────────
// Main StepResults
// ─────────────────────────────────────────────────────────
export default function StepResults({ wizardData, patient, onRestart, onClose }) {
  const printRef = useRef(null);

  const symptoms   = wizardData.symptoms   || [];
  const screening  = wizardData.screening  || {};
  const triage     = wizardData.triage     || {};
  const scoreData  = screening.scoreData;
  const hasScoring = scoreData && Array.isArray(scoreData.results) && scoreData.results.length > 0;

  const triageCode     = triage.triageCode || triage.code || "routine";
  const tMeta          = TRIAGE_META[triageCode] || TRIAGE_META.routine;
  const bodySystems    = computeBodySystems(symptoms);
  const recommendations = buildRecommendations(triageCode, screening);

  const reportId = `RPT-${Date.now().toString().slice(-8)}`;
  const reportDate = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className={styles.root} ref={printRef}>

      {/* ── Report Header ── */}
      <div className={styles.reportHeader}>
        <div className={styles.reportHeaderLeft}>
          <div className={styles.reportBrand}>FemCare Health</div>
          <h1 className={styles.reportTitle}>Symptom Assessment Report</h1>
          <p className={styles.reportMeta}>
            {reportDate} · ID: {reportId} · Patient: {patient?.name || "Unknown"}
          </p>
        </div>
        <div className={styles.reportHeaderRight}>
          <button className={styles.headerBtn} onClick={() => window.print()}>🖨 Print</button>
          <button className={styles.headerBtn}>⬇ Download PDF</button>
          {onClose && (
            <button className={`${styles.headerBtn} ${styles.headerBtnClose}`} onClick={onClose}>✕ Close</button>
          )}
        </div>
      </div>

      {/* ── Disclaimer ── */}
      <div className={styles.disclaimer}>
        <span>ℹ️</span>
        <p>
          <strong>This report is for informational purposes only and does not constitute a medical diagnosis.</strong>
          {" "}Please consult a qualified healthcare professional for clinical evaluation and treatment.
        </p>
      </div>

      {/* ── Triage Result ── */}
      <div className={styles.section}>
        <SectionHeading icon="⚡" title="Triage Assessment" />
        <TriageBanner triage={triage} />
      </div>

      {/* ── Screening Scores ── */}
      {hasScoring && (
        <div className={styles.section}>
          <SectionHeading icon="📊" title="Clinical Screening Results" />

          {/* Aggregate summary */}
          <div className={styles.aggregateBar}>
            <div className={styles.aggregateStat}>
              <div className={styles.aggregateNum} style={{ color: tMeta.color }}>
                {scoreData.totalScore}
              </div>
              <div className={styles.aggregateLbl}>Total Score</div>
            </div>
            <div className={styles.aggregateDivider} />
            <div className={styles.aggregateStat}>
              <div className={styles.aggregateNum}>{scoreData.maxScore}</div>
              <div className={styles.aggregateLbl}>Max Possible</div>
            </div>
            <div className={styles.aggregateDivider} />
            <div className={styles.aggregateStat}>
              <div className={styles.aggregateNum} style={{ color: tMeta.color }}>
                {pct(scoreData.totalScore, scoreData.maxScore)}%
              </div>
              <div className={styles.aggregateLbl}>Severity %</div>
            </div>
            <div className={styles.aggregateDivider} />
            <div className={styles.aggregateStat}>
              <div className={styles.aggregateNum} style={{ color: SEVERITY_COLOR[scoreData.highest?.severity] || "#6B7280", fontSize: 13 }}>
                {scoreData.highest?.severity || "—"}
              </div>
              <div className={styles.aggregateLbl}>Highest Severity</div>
            </div>
          </div>

          {/* Per-assessment cards */}
          <div className={styles.scoreCards}>
            {scoreData.results.map((r) => r && (
              <ScreeningScoreCard key={r.questionnaireId} result={r} />
            ))}
          </div>
        </div>
      )}

      {/* ── Reported Symptoms ── */}
      <div className={styles.section}>
        <SectionHeading icon="🔍" title={`Reported Symptoms (${symptoms.length})`} />
        {symptoms.length > 0 ? (
          <SymptomList symptoms={symptoms} />
        ) : (
          <p className={styles.emptyText}>No symptoms recorded.</p>
        )}
      </div>

      {/* ── Body Systems ── */}
      {bodySystems.length > 0 && (
        <div className={styles.section}>
          <SectionHeading icon="🫀" title="Body Systems Involved" />
          <div className={styles.bodySystems}>
            {bodySystems.map((sys) => <BodySystemRow key={sys.system} sys={sys} />)}
          </div>
        </div>
      )}

      {/* ── Recommendations ── */}
      {/* <div className={styles.section}>
        <SectionHeading icon="✅" title="Recommended Next Steps" />
        <div className={styles.recommendationsList}>
          {recommendations.map((rec, i) => (
            <div key={i} className={styles.recommendationRow}>
              <div className={styles.recommendationDot} style={{ background: tMeta.color }}>
                <svg width="9" height="9" fill="none" stroke="white" strokeWidth={3} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className={styles.recommendationText}>{rec}</span>
            </div>
          ))}
        </div>
      </div> */}
      <div className={styles.resultsQuickActionsWrap}>
  <div className={styles.resultsQuickActionsHeader}>
    <h3 className={styles.resultsQuickActionsTitle}>
      Recommended Next Actions 
    </h3>

    <p className={styles.resultsQuickActionsSubtitle}>
      Continue your care journey with specialist guidance and personalized support.
    </p>
  </div>

  <div className={styles.resultsActionsGrid}>
    {[
      {
        title: "Book Appointment",
        desc: "Consult a specialist doctor",
        bg: "#F8F9F9",
        icon: "📅",
        link: "/book-appointment",
      },
      {
        title: "Chat with Expert",
        desc: "Get guidance and support",
        bg: "#E3F2FD",
        icon: "💬",
        link: "/chat",
      },
      {
        title: "Enroll Program",
        desc: "Personalized wellness plans",
        bg: "#FEF9E7",
        icon: "✨",
        link: "/programs",
      },
    ].map((action, idx) => (
      <Link
        key={idx}
        to={action.link}
        className={styles.resultsActionCard}
        style={{ background: action.bg }}
      >
        <div className={styles.resultsActionIcon}>
          {action.icon}
        </div>

        <div className={styles.resultsActionBody}>
          <div className={styles.resultsActionTitle}>
            {action.title}
          </div>

          <div className={styles.resultsActionDesc}>
            {action.desc}
          </div>
        </div>

        <div className={styles.resultsActionArrow}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </div>
      </Link>
    ))}
  </div>
</div>


      {/* {patient && (
        <div className={styles.section}>
          <SectionHeading icon="👤" title="Patient Information" />
          <div className={styles.patientGrid}>
            {[
              ["Name",       patient.name],
              ["Age",        patient.age ? `${patient.age} yrs` : "—"],
              ["Gender",     patient.gender],
              ["Location",   patient.location],
              ["Weight",     patient.weightKg ? `${patient.weightKg} kg` : "—"],
              ["Height",     patient.heightCm ? `${patient.heightCm} cm` : "—"],
              ["Blood Group",patient.bloodGroup],
            ].map(([label, value]) => (
              <div key={label} className={styles.patientCell}>
                <div className={styles.patientLabel}>{label}</div>
                <div className={styles.patientValue}>{value || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )} */}

      {/* ── Footer actions ── */}
      {/* <div className={styles.footerActions}>
        <button className={styles.restartBtn} onClick={onRestart}>
          Start New Assessment
        </button>
        <a href="/book-appointment" className={styles.bookBtn}>
          Book a Specialist →
        </a>
      </div>

      <div className={styles.footerNote}>
        Report ID: {reportId} · Generated on {reportDate} · FemCare Health Platform
      </div> */}
    </div>
  );
}



